import React from "react";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";

/** What a click on another step in the rail does on the current stage. */
export type OnboardingStepLeave = (target: OnboardingFlowStage) => void;

/**
 * Where the current stage registers its {@link OnboardingStepLeave}; the shell
 * provides it. Without a registration (the intro) a jump opens the target
 * directly.
 */
export const OnboardingStepLeaveContext = React.createContext<{
  current: OnboardingStepLeave | null;
} | null>(null);

/**
 * Lets a jump from the rail behave like the stage's own Continue: the stage
 * validates, saves what it saves on Continue and then opens `target`, or stays
 * and shows what is wrong.
 */
export function useOnboardingStepLeave(leave: OnboardingStepLeave): void {
  const registry = React.useContext(OnboardingStepLeaveContext);
  // The latest handler, so the registration needs no update on every render.
  const latest = React.useRef(leave);
  React.useLayoutEffect(() => {
    latest.current = leave;
  });
  React.useEffect(() => {
    if (!registry) {
      return;
    }
    const registered: OnboardingStepLeave = target => latest.current(target);
    registry.current = registered;
    return () => {
      if (registry.current === registered) {
        registry.current = null;
      }
    };
  }, [registry]);
}
